import React from "react";
import {
  X,
  Check,
  Loader2,
  RefreshCw,
  MapPinHouse,
  CircleAlert,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { useDebounce } from "../../hooks/use-debounce";

import { cityNameSearch } from "../../api/pricing/action";

import {
  setNewCityValue,
  setNewCityValueCorrect,
} from "../../redux/pricing/slice";

export function CitySearch({ className, ...props }) {
  const dispatch = useDispatch();

  const newCityCreationForm = useSelector(
    ({ pricing }) => pricing.newCityCreationForm
  );

  const newCityValue = newCityCreationForm.cityName;
  const newCityValueCorrect = newCityCreationForm.cityNameCorrect;
  const cityNameErrorMessage = newCityCreationForm.cityNameErrorMessage;
  const cityExistErrorMessage = newCityCreationForm.cityExistErrorMessage;

  const [cities, setCities] = React.useState([]);
  const [isError, setIsError] = React.useState(false);
  const [refetchKey, setRefetchKey] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDropDownVisible, setIsDropDownVisible] = React.useState(false);

  const debouncedCityValue = useDebounce(newCityValue); // default debounce of 500ms

  React.useEffect(() => {
    if (debouncedCityValue.length < 3) {
      setCities([]);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    cityNameSearch(debouncedCityValue)
      .then((data) => {
        setCities(data);
        setIsError(false);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [debouncedCityValue, refetchKey]);

  const handleChange = (e) => {
    setIsDropDownVisible(true);

    dispatch(setNewCityValueCorrect({ value: false }));
    dispatch(
      setNewCityValue({
        cityName: e.target.value,
        country: "",
      })
    );
  };

  return (
    <div className={className} {...props}>
      <div className="relative z-10">
        <Input
          id="city-search"
          autoComplete="off"
          value={newCityValue}
          onChange={handleChange}
          placeholder="Type to Search City"
          onFocus={() => setIsDropDownVisible(true)}
          onBlur={() => setTimeout(() => setIsDropDownVisible(false), 300)}
          className="focus-visible:ring-offset-0 bg-[#131821] text-[#4E5665] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A] px-8"
        />

        <Label
          htmlFor="city-search"
          className="absolute left-3 top-1/2 -translate-y-1/2"
        >
          <MapPinHouse className="size-4 hover:cursor-pointer text-[#B3B3B3]" />
        </Label>

        {/* Dropdown */}
        {isDropDownVisible && (
          <div className="border-[#353C4A] absolute mt-2 w-full rounded-md border bg-popover text-popover-foreground shadow-sm">
            <Command>
              <CommandList className="bg-[#181E29] text-[#4E5665] focus-visible:ring-offset-0 max-h-40 overflow-y-auto scrollbar-none">
                {/* Loading State */}
                {isLoading && (
                  <CommandEmpty>
                    <div className="flex items-center justify-center">
                      <Loader2 className="size-4 animate-spin" />
                    </div>
                  </CommandEmpty>
                )}

                {/* Error State */}
                {isError && (
                  <CommandEmpty>
                    <div className="flex flex-col items-center justify-center gap-2">
                      <p className="font-medium text-red-500">
                        Unable to Fetch Cities
                      </p>
                      <Button
                        variant="ghost"
                        onClick={() => setRefetchKey((prev) => prev + 1)}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className="size-4" /> Retry
                      </Button>
                    </div>
                  </CommandEmpty>
                )}

                {/* No Results */}
                {!isError &&
                  !isLoading &&
                  cities.length === 0 &&
                  debouncedCityValue.length >= 3 && (
                    <CommandEmpty>No City found.</CommandEmpty>
                  )}

                {/* Default Message */}
                {!isError && !isLoading && debouncedCityValue.length < 3 && (
                  <CommandEmpty>Type to Search City</CommandEmpty>
                )}

                {/* City List */}
                {!isError && !isLoading && cities.length > 0 && (
                  <CommandGroup>
                    {cities.map((option) => (
                      <CommandItem
                        className="font-medium hover:cursor-pointer"
                        key={`${option.cityName},${option.country}`}
                        onSelect={() => {
                          dispatch(setNewCityValueCorrect({ value: true }));
                          dispatch(
                            setNewCityValue({
                              cityName: option.cityName,
                              country: option.country,
                            })
                          );
                        }}
                      >
                        <div className="truncate">
                          {option.cityName}, {option.country}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </div>
        )}

        {/* Correct City Indicator */}
        {!(cityNameErrorMessage || cityExistErrorMessage) &&
          newCityValueCorrect && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500 hover:cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="border border-transparent bg-slate-700 text-xs text-white"
                >
                  <p>City Name is Valid</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

        {/* Incorrect Indicator Icon */}
        {(cityNameErrorMessage || cityExistErrorMessage) && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <X className="absolute right-3 top-1/2 h-4  w-4 -translate-y-1/2 text-red-500 hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border border-transparent bg-slate-700 text-xs text-white"
              >
                {cityNameErrorMessage || cityExistErrorMessage}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Error Indicator */}
        {newCityValueCorrect && isError && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleAlert className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500 hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-slate-700 text-xs text-white"
              >
                <p>Error fetching city names</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {cityNameErrorMessage && (
        <p className="text-sm text-red-300 mt-2">{cityNameErrorMessage}</p>
      )}

      {cityExistErrorMessage && (
        <p className="text-red-300 mt-2">{cityExistErrorMessage}</p>
      )}
    </div>
  );
}
